import { z } from 'zod';

/**
 * Shared contract for programmatic API keys — used by BOTH the backend
 * (validation, scope enforcement in checkJwt) and the frontend (scope picker,
 * key list). Single source of truth so the two can never drift.
 *
 * A scope is `<resource>:<action>` where action ∈ {read, write}:
 *   '*'            full access      '*:read'        read-only everywhere
 *   'clients:*'    all on clients   'clients:read'  exactly read on clients
 */
export const FULL_SCOPE = '*';

export type Action = 'read' | 'write';

/** Resources a key can be scoped to (the public API surface). Extend as new
 *  public route groups ship. */
export const SCOPE_RESOURCES = [
  'clients', // contacts
  'messages',
  'templates',
  'campaigns',
  'chatbots',
  'groups',
  'calls',
  'schedule',
  'org',
  'embed',
  'journeys', // journey events ingest (third-party integrations)
] as const;

export type ScopeResource = (typeof SCOPE_RESOURCES)[number];

export const SCOPE_PRESETS = {
  full: [FULL_SCOPE],
  read_only: ['*:read'],
} as const;

const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const SKIP_SEGMENT = /^(?:api|v\d+)$/;

/** First meaningful path segment, skipping `api`/`vN` prefixes. */
const resourceFromPath = (path: string): string | null => {
  const segments = (path.split('?')[0] ?? '').split('/').filter(Boolean);
  let i = 0;
  while (i < segments.length && SKIP_SEGMENT.test(segments[i])) i++;
  return segments[i] ?? null;
};

/** The scope a request needs, or null when no resource can be derived. */
export const requiredScopeFor = (
  method: string,
  path: string,
): string | null => {
  const resource = resourceFromPath(path);
  if (!resource) return null;
  const action = READ_METHODS.has(method.toUpperCase()) ? 'read' : 'write';
  return `${resource}:${action}`;
};

/** Resources that programmatic API keys may never reach — managed through the
 *  dashboard only, so even a full-access ('*') key is refused. */
export const API_KEY_FORBIDDEN_RESOURCES: readonly string[] = [
  'webhooks',
  'flows',
];

/** True when `path` targets a resource API keys are never allowed to use. */
export const isApiKeyForbiddenPath = (path: string): boolean => {
  const resource = resourceFromPath(path);
  return resource !== null && API_KEY_FORBIDDEN_RESOURCES.includes(resource);
};

/** Whether a key holding `scopes` is allowed to perform `required`. */
export const hasScope = (
  scopes: string[] | null | undefined,
  required: string | null,
): boolean => {
  if (!scopes || scopes.length === 0) return false;
  if (scopes.includes(FULL_SCOPE)) return true;
  if (!required) return false;
  if (scopes.includes(required)) return true;
  const [resource, action] = required.split(':');
  return scopes.includes(`${resource}:*`) || scopes.includes(`*:${action}`);
};

const SCOPE_RE = /^(?:\*|[a-z][a-z0-9-]*)(?::(?:\*|read|write))?$/;

/** Correct shape AND a known resource (or `*`). */
export const isValidScope = (scope: string): boolean => {
  if (scope === FULL_SCOPE) return true;
  if (!SCOPE_RE.test(scope)) return false;
  const resource = scope.split(':')[0];
  return (
    resource === '*' ||
    (SCOPE_RESOURCES as readonly string[]).includes(resource)
  );
};

/** Build scope strings from a per-resource selection map (frontend picker). */
export const scopesFromSelection = (
  selection: Record<string, { read: boolean; write: boolean }>,
): string[] => {
  const scopes: string[] = [];
  for (const [resource, actions] of Object.entries(selection)) {
    if (actions.read && actions.write) scopes.push(`${resource}:*`);
    else if (actions.read) scopes.push(`${resource}:read`);
    else if (actions.write) scopes.push(`${resource}:write`);
  }
  return scopes;
};

/** Human label for a stored scope array (key list display). */
export const describeScopes = (scopes: string[] | null | undefined): string => {
  if (!scopes || scopes.length === 0) return 'No access';
  if (scopes.includes(FULL_SCOPE)) return 'Full access';
  if (scopes.length === 1 && scopes[0] === '*:read') return 'Read-only';
  return scopes.join(', ');
};

// ── request / response contract ──────────────────────────────────────────────

export const createApiKeySchema = z
  .object({
    name: z.string().min(1, 'name is required').max(100),
    scopes: z
      .array(z.string().refine(isValidScope, 'invalid scope'))
      .min(1)
      .optional(),
    preset: z.enum(['full', 'read_only']).optional(),
    expiresInDays: z.number().int().positive().max(3650).optional(),
  })
  .strict()
  .refine(d => !!d.scopes || !!d.preset, {
    message: 'Provide either an explicit `scopes` array or a `preset`',
  });

export type CreateApiKeyBody = z.infer<typeof createApiKeySchema>;

/** Masked key as returned to clients — never includes the raw token/hash. */
export interface ApiKeyRecord {
  id: string;
  name: string | null;
  prefix: string | null;
  scopes: string[] | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
}

/** Create response — carries the raw secret exactly once. */
export interface CreatedApiKey extends ApiKeyRecord {
  key: string;
}

/** A dashboard login session, as shown in the "your devices / active sessions" view. */
export interface SessionRecord {
  id: string;
  employeeId: number | null;
  employeeName?: string | null;
  employeeEmail?: string | null;
  businessId?: number | null;
  businessName?: string | null;
  businessPhoneNumber?: string | null;
  /** Only on the caller's OWN list, which spans every org they belong to. */
  orgName?: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
}
